/**
 * The two graphics choices a player can make, remembered between visits.
 *
 * A page-lifetime singleton, like `carAudio` and `musicPlayer`: the Escape menu writes it and
 * whichever game is running reads it, and neither has to know the other exists. A game is
 * built and thrown away for every race; the preference outlives all of them.
 */

/** See `scene.ts`: `low` draws fewer pixels and skips the daytime post chain. */
export type Quality = 'high' | 'low';

/**
 * Where the chase camera sits. `standard` is high and back — it shows the road ahead over
 * the car, which is what you race by. `low` is down at the bumper and close, with a
 * narrower lens, which puts the street's walls and the sky in the frame instead of tarmac.
 */
export type ChaseView = 'standard' | 'low';

/**
 * How far the chase camera draws. `far` is all of it, as it always was; the other two stop
 * the city at a distance and close the haze in ahead of it (DECISIONS.md §92). Only the chase
 * camera: the views from the air always see the whole region, which is the point of them.
 */
export type ViewDistance = 'near' | 'medium' | 'far';

/** Metres for each, or null for no limit. */
export const VIEW_DISTANCE_METRES: Record<ViewDistance, number | null> = {
  near: 450,
  medium: 800,
  far: null,
};

const QUALITY_KEY = 'osmRacer.quality';
const CHASE_KEY = 'osmRacer.chaseView';
const DISTANCE_KEY = 'osmRacer.viewDistance';

type Listener = () => void;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Storage can be off, or throw in a private window; the defaults are fine.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not remembered, then. It still applies for this visit.
  }
}

/**
 * What the URL says, else what was saved, else a guess from the kind of screen — and whether
 * anybody actually said, which is what decides whether the game may change it on its own.
 */
function initialQuality(): { quality: Quality; said: boolean } {
  if (typeof location !== 'undefined') {
    const asked = new URLSearchParams(location.search).get('quality');
    if (asked === 'high' || asked === 'low') return { quality: asked, said: true };
  }
  const saved = read(QUALITY_KEY);
  if (saved === 'high' || saved === 'low') return { quality: saved, said: true };
  // A coarse pointer is a phone or a tablet, and so is its GPU.
  const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  return { quality: coarse ? 'low' : 'high', said: false };
}

const initial = initialQuality();

function initialDistance(): ViewDistance {
  const saved = read(DISTANCE_KEY);
  return saved === 'near' || saved === 'medium' ? saved : 'far';
}

class GraphicsPrefs {
  private qualityNow: Quality = initial.quality;
  /** Whether the quality is somebody's choice rather than the game's guess. */
  private said = initial.said;
  private chaseNow: ChaseView = read(CHASE_KEY) === 'low' ? 'low' : 'standard';
  private distanceNow: ViewDistance = initialDistance();
  private readonly listeners = new Set<Listener>();

  get quality(): Quality {
    return this.qualityNow;
  }

  get chaseView(): ChaseView {
    return this.chaseNow;
  }

  get viewDistance(): ViewDistance {
    return this.distanceNow;
  }

  setViewDistance(distance: ViewDistance): void {
    if (distance === this.distanceNow) return;
    this.distanceNow = distance;
    write(DISTANCE_KEY, distance);
    this.emit();
  }

  setQuality(quality: Quality): void {
    // Written even when it is already the setting: picking `high` while on the guessed `high`
    // is still a choice, and it is what stops `lowerForSpeed` overruling it next time.
    this.said = true;
    write(QUALITY_KEY, quality);
    if (quality === this.qualityNow) return;
    this.qualityNow = quality;
    this.emit();
  }

  /**
   * The game's own downgrade, for a machine that cannot hold the frame rate on `high`. Only
   * ever from the guess: a player who chose `high` in the menu keeps it, however it runs.
   * Remembered, so the next race starts where this one ended up. Returns whether it acted.
   */
  lowerForSpeed(): boolean {
    if (this.said || this.qualityNow === 'low') return false;
    this.setQuality('low');
    return true;
  }

  setChaseView(view: ChaseView): void {
    if (view === this.chaseNow) return;
    this.chaseNow = view;
    write(CHASE_KEY, view);
    this.emit();
  }

  /** Returns the unsubscribe. */
  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const graphicsPrefs = new GraphicsPrefs();
